

import { IconAlertTriangle, IconExternalLink, IconX } from "@tabler/icons-react";
import { useLocale, useTranslations } from "next-intl";
import { safeLanguageName } from "@/lib/utils/languages";
import { Card } from "../Card/Card";
import { useEffect, useMemo, useRef, useState } from "react";
import { OptionInput } from "../Inputs/OptionInput";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { CollapsibleSection } from "@/components/CollapsibleSection/CollapsibleSection";
import { FormInput } from "../Inputs/FormInput";
import { SliderInput } from "../Inputs/SliderInput";
import { Preview } from "../PSendEmail/components/Preview";
import { AttachmentPreview } from "../PSendEmail/components/AttachmentPreview";
import { AttachmentPreflightWarningBlock } from "../PSendEmail/AttachmentPreflightWarningBlock";
import { useAttachmentEmailPreflight } from "@/hooks/useAttachmentEmailPreflight";
import {
  resolveLangValue,
  resolveLangKey,
  resolveLangKeyFromMap,
} from "@/lib/utils/resolve-lang";
import { buildPreviewFormatContext } from "@/lib/utils/additional-field-format";
import { useFieldsOptional } from "@/providers/FieldsProvider";
import { useEventTimezone } from "@/providers/EventProvider";
import { useTestMail } from "../PSendEmail/hooks/useTestMail";
import { motion } from "framer-motion";
import { SHEET_INITIAL, SHEET_ANIMATE, SHEET_EXIT, SHEET_SPRING } from "@/components/Modal/sheetMotion";
import { createPortal } from "react-dom";
import { Guest } from "@/types/Guest";
import { EmailTemplate } from "@/types/EmailTemplate";
import { SenderAddress } from "@/types/SenderAddress";
import { EmailDomain } from "@/types/EmailDomain";
import {
  TemplateData,
  buildTemplateData,
  resolveRecipientLanguage,
} from "../PSendEmail/utils/template-utils";
import { RecipientLanguageChip } from "../PSendEmail/components/RecipientLanguageChip";
import { SenderFromLine } from "../PSendEmail/components/SenderFromLine";
import {
  resolveEffectiveSender,
  isPinnedSenderDomainUnverified,
  senderAddressLiteral,
} from "@/lib/utils/sender-display";
import { useWhiteLabelAccess } from "@/hooks/useWhiteLabelAccess";
import { getEmailTemplatesByEvent } from "@/lib/api/handlers/EmailTemplateHandling";
import {
  getEmailsMetaInformation,
  type EmailsMetaInformation,
} from "@/lib/api/handlers/SenderAddressHandling";
import { submitBackgroundJob } from "@/lib/api/handlers/BackgroundJobHandling";
import { useClientRequestId } from "@/hooks/useClientRequestId";
import { useBackgroundJobs } from "@/providers/BackgroundJobsProvider";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { useOnboarding } from "@/providers/OnboardingProvider";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import type { ServerError } from "@/lib/api/errors/ErrorHandler";
import { replaceVariablesInText } from "@/lib/utils/variable-replacer";
import { useEventVariables } from "@/hooks/useEventVariables";
import { useMainGuestVariables } from "@/hooks/useMainGuestVariables";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { VideoPlayerRiv } from "@/components/VideoPlayer/VideoPlayerRiv";
import { OptInWarningBlock } from "../PSendEmail/OptInWarningBlock";
import { SendClassPicker } from "../PSendEmail/SendClassPicker";
import {
  canChooseSendClass,
  sendClassParams,
  type SendClass,
} from "@/lib/email/sendClass";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";
import { SM_UP_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";

interface PSendEmailToGuestProps {
  onClose: () => void;
  guest: Guest;
  eventId: string;
  eventName: string;
}

  // How stale a read has to be before a window focus is worth another one. Each
  // read pulls the FULL html map of every template in the event, and a focus
  // fires on every alt-tab, DevTools open and OS file picker — not only after a
  // trip to the builder. Mirrors the 30s `staleTime` the react-query lane that
  // feeds the normal-mode dialog already applies.
  const REFETCH_MIN_INTERVAL_MS = 30_000;

export const PSendEmailToGuest: React.FC<PSendEmailToGuestProps> = ({
  onClose,
  guest,
  eventId,
  eventName,
}) => {
  useSuppressCoach(); // hide bottom-right floaters over this modal
  const t = useTranslations("pSendEmailToGuest");
  // Bind the `mainGuest` source (fetches the main guest if this companion's DTO
  // wasn't loaded) then merge event-level vars so the subject resolves
  // {{mainGuest.*}} + {{eventDate}}/{{eventTime}}/{{eventLocation}} … like a
  // real send (the body preview enriches separately inside <Preview>).
  const { enrich: enrichWithEventVars } = useEventVariables(eventId);
  const guestWithMain = useMainGuestVariables(eventId, guest);
  const enrichedGuest = useMemo(
    () => enrichWithEventVars(guestWithMain ?? guest),
    [enrichWithEventVars, guestWithMain, guest],
  );
  // Send-out name keys live in the shared pSendEmail namespace (reused across
  // all three send paths) so we don't duplicate them per component.
  const tSend = useTranslations("pSendEmail");
  const locale = useLocale();
  // Render template language codes via Intl (see PSendEmail).
  // safeLanguageName, NOT getDisplayNameFromLocale: `allLanguageCodes` now
  // includes the raw KEYS of the template's html map (see its construction
  // below), and those are ordinary database strings — `Intl.DisplayNames.of()`
  // raises RangeError on `de_DE`, `" de-DE"`, `de--DE`, and anything else that
  // is not a well-formed tag. There is no ErrorBoundary between here and the
  // app-level one, so a throw replaces the whole page mid-compose.
  const langName = (code: string) => safeLanguageName(code, locale);
  const tErrors = useTranslations("errors");
  const tOptIn = useTranslations("pSendEmailValidation");
  // The bounce copy lives in the guest-detail namespace and is reused verbatim
  // here — same wording the panel shows, no second set of keys to drift apart.
  const tGuest = useTranslations("guest-details");

  const [fetchLoading, setFetchLoading] = useState(true);
  const [sendLoading, setSendLoading] = useState(false);
  // Holds the whole ServerError (not just its message) so the raw backend
  // `detail` survives to translateBackendError below.
  const [error, setError] = useState<string | ServerError>("");
  // Separate from `error`, which belongs to the SEND. One shared slot meant a
  // successful focus refetch retracted the message from a failed blast — the
  // admin then either believes it went out, or fires a second one on top of a
  // partially-succeeded first.
  const [fetchError, setFetchError] = useState<string | ServerError>("");
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [senderAddresses, setSenderAddresses] = useState<SenderAddress[]>([]);
  const [domains, setDomains] = useState<EmailDomain[]>([]);
  // Meta unreadable (missing capability) — see PSendEmail.
  const [senderDataUnavailable, setSenderDataUnavailable] = useState(false);
  const [data, setData] = useState<TemplateData | null>(null);
  // Refs readable from the focus-refetch listener, whose closure is built once
  // per mount and would otherwise see the mount-time values forever.
  const dataRef = useRef<TemplateData | null>(null);
  dataRef.current = data;
  const lastFetchAtRef = useRef(0);
  const sendLoadingRef = useRef(false);
  sendLoadingRef.current = sendLoading;
  // The template object `data` was last built from — see the re-derive effect.
  const lastTemplateSourceRef = useRef<EmailTemplate | null>(null);
  // Attachment preflight for THIS fixed recipient — warn-only, fails soft.
  const { warnings: preflightWarnings, loading: preflightLoading } =
    useAttachmentEmailPreflight({
      eventId,
      emailTemplateId: data?.emailTemplateId,
      guestId: guest?.guestId,
      enabled: !!data?.emailTemplateId,
    });
  // Type-aware formatting for `{{additionalFields.x}}` in the SUBJECT preview so
  // it matches the sent subject, in the recipient's resolved render language.
  // `useFieldsOptional` degrades gracefully if this modal is ever mounted outside
  // a FieldsProvider (no crash, just raw values).
  const fieldsCtx = useFieldsOptional();
  const subjectEventTimezone = useEventTimezone();
  // The address this send will REALLY come from — same three-rung chain the
  // worker walks (see PSendEmail).
  const { hasWhiteLabel, isResolved } = useWhiteLabelAccess(eventId);
  const whiteLabel = isResolved ? hasWhiteLabel : null;
  // `null` = unreadable; the From line then says so (SenderFromLine).
  const effectiveSender = senderDataUnavailable
    ? null
    : resolveEffectiveSender({
        templateSenderMail: data?.emailId || undefined,
        // A platform-pinned template resolves to the Veltric address no matter
        // what the event's star says — mirror the worker here or the dialog
        // promises a From the recipient will never see.
        platformSenderEnabled: data?.platformSenderEnabled,
        senderAddresses,
        domains,
        hasWhiteLabel: whiteLabel,
      });
  // See PSendEmail: the worker fails the whole job when the pinned sender's
  // domain is not VERIFIED, and this dialog submits the same job shape.
  const senderBlocked = isPinnedSenderDomainUnverified(
    data?.emailDomainId,
    domains,
  );
  const blockedSender = senderBlocked
    ? (senderAddresses.find((s) => s.senderAddressId === data?.emailId) ?? null)
    : null;
  const blockedSenderAddress = blockedSender
    ? senderAddressLiteral(blockedSender, domains)
    : null;
  // EFFECTIVE render language — the key the BODY resolves to, mirroring the
  // worker's `effLang` (`email-trigger-service.ts:562`). Map-based, because the
  // worker decides from the content map: a declared-but-empty variant is not a
  // variant, and the primary gets its own base step.
  const subjectEffLang = useMemo(
    () =>
      resolveLangKeyFromMap(data?.html, guest.language, data?.truePrimaryLanguage ?? "") ??
      guest.language ??
      data?.truePrimaryLanguage ??
      "",
    [data?.html, guest.language, data?.truePrimaryLanguage],
  );
  const subjectCtx = useMemo(
    () =>
      buildPreviewFormatContext(
        fieldsCtx?.allFieldsUnfiltered ?? [],
        subjectEventTimezone,
        subjectEffLang,
      ),
    [fieldsCtx, subjectEventTimezone, subjectEffLang],
  );
  const [markAsContacted, setMarkAsContacted] = useState(true);
  // Marketing vs organisational notice — offered to OWNER/ADMIN only; every
  // other role sends MARKETING without seeing the choice (see PSendEmail).
  // Only while the backend's switch is on (`organisationalSendEnabled`, H2).
  const capabilities = useCapabilitiesOptional();
  const canPickSendClass = canChooseSendClass(
    capabilities.role,
    capabilities.organisationalSendEnabled,
  );
  const [pickedSendClass, setPickedSendClass] = useState<SendClass>("MARKETING");
  const [sendClassAttested, setSendClassAttested] = useState(false);
  // A send was tried without the tick — only then is the box flagged.
  const [sendClassAttempted, setSendClassAttempted] = useState(false);
  const sendClass: SendClass = canPickSendClass ? pickedSendClass : "MARKETING";
  const organisational = sendClass === "ORGANISATIONAL";
  const attestationMissing = organisational && !sendClassAttested;
  const attestationHintShown = attestationMissing && sendClassAttempted;
  const flagMissingAttestation = () => {
    setSendClassAttempted(true);
    document
      .querySelector<HTMLElement>('[data-testid="send-class-attestation"]')
      ?.focus();
  };
  // The picked template vanished from a refreshed list — see the re-derive
  // effect. Same stake as the bulk lane: the quota is charged before the worker
  // discovers there is nothing to render.
  const [templateGone, setTemplateGone] = useState(false);

  // Optional send-out name, pre-filled with a date/time default. Persisted onto
  // the guest's EmailSent row so the timeline shows which send they received.
  // Fixed when the dialog opens, not recomputed per render: it is also the
  // fallback for a CLEARED name field, and the submit payload — which the
  // idempotency key is bound to (useClientRequestId) — must not change
  // between a failed send and its retry just because the minute rolled over.
  const [defaultSendName] = useState<string>(() =>
    tSend("sendNameDefault", {
      date: new Date().toLocaleString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    }),
  );
  const [sendName, setSendName] = useState<string>(defaultSendName);

  const templateInputRef = useRef<HTMLInputElement>(null);
  const languageInputRef = useRef<HTMLInputElement>(null);
  const [testMailLanguage, setTestMailLanguage] = useState("");

  const { addJob } = useBackgroundJobs();
  const { completeStep } = useOnboarding();

  // Submit idempotency key: one per logical send, bound to the payload it is
  // minted for — replayed on a retry of the SAME payload after a ServerError,
  // rotated by any edit (template, options), retired after a success (see
  // useClientRequestId).
  const clientRequestId = useClientRequestId();

  const {
    testMailValue,
    testMailError,
    testMailLoading,
    handleTestMailChange,
    handleSendTestMail,
  } = useTestMail();

  // Fetch templates and sender meta on mount, and again on window focus — the
  // "open in builder" link sends the admin to another tab to fix a translation,
  // and a dialog still showing the mount-time copy would be exactly the stale
  // preview that link exists to cure.
  useEffect(() => {
    let cancelled = false;
    async function fetchData(opts?: { throttled?: boolean }) {
      // Never re-read underneath an in-flight send.
      if (sendLoadingRef.current) return;
      // Throttle only THROTTLED (focus-driven) reads, and stamp the window
      // BEFORE the awaits so a burst of focus events in one tick collapses to a
      // single read. The mount fetch deliberately does not stamp: the first
      // return from the builder must always re-read, which is the whole flow the
      // deep link exists for. A failed read un-stamps below, so an outage cannot
      // block retries for 30s while its own error sits under the send button.
      if (opts?.throttled) {
        if (Date.now() - lastFetchAtRef.current < REFETCH_MIN_INTERVAL_MS) return;
        lastFetchAtRef.current = Date.now();
      }
      const unstampOnFailure = () => {
        if (opts?.throttled) lastFetchAtRef.current = 0;
      };
      const [templatesResult, metaResult] = await Promise.all([
        getEmailTemplatesByEvent(
          eventId,
          "templateName,mailSubject,emailTemplateId,senderMail,platformSenderEnabled,replyToMail,bccMail,primaryLanguage,languages,html,contentAreaBackgroundColor",
        ),
        getEmailsMetaInformation(eventId),
      ]);

      if (cancelled) return;

      if (
        typeof templatesResult === "object" &&
        "isServerError" in templatesResult
      ) {
        setFetchError(templatesResult);
        unstampOnFailure();
        setFetchLoading(false);
        return;
      }
      if (
        typeof metaResult === "object" &&
        "isServerError" in metaResult
      ) {
        setFetchError(metaResult);
        unstampOnFailure();
        setFetchLoading(false);
        return;
      }

      const fetchedTemplates = templatesResult as EmailTemplate[];
      const {
        senderAddresses: sa,
        emailDomains: ed,
        senderAccessDenied,
        domainAccessDenied,
      } = metaResult as EmailsMetaInformation;

      // A read that WORKED retracts the last read that did not — and only that.
      setFetchError("");
      setTemplates(fetchedTemplates);
      setSenderAddresses(sa);
      setDomains(ed);
      setSenderDataUnavailable(senderAccessDenied || domainAccessDenied);

      // Auto-select first non-automatic template — but ONLY on the first read.
      // On a focus refetch the admin has usually picked a template already, and
      // snapping back to the alphabetically first one would silently retarget
      // the send. The re-derive effect below refreshes their pick instead.
      const manualTemplates = fetchedTemplates.filter((t) => !t.automaticSend);
      if (manualTemplates.length > 0 && dataRef.current === null) {
        setData(buildTemplateData(manualTemplates[0], sa, ed, guest.language));
      }

      setFetchLoading(false);
    }
    fetchData();
    const onFocus = () => {
      void fetchData({ throttled: true });
    };
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [eventId, guest.language]);

  // Re-derive the picked template's data when the template list changes.
  // Refetching alone changes nothing visible: `data` is a snapshot taken when
  // the template was picked. Everything in it is template-derived (`sendName`
  // and `markAsContacted` are separate state), so rebuilding loses no input.
  useEffect(() => {
    const templateId = data?.emailTemplateId;
    if (!templateId) return;
    const source = templates.find((tpl) => tpl.emailTemplateId === templateId);
    // Deleted underneath us. Keep the stale `data` — blanking a composed dialog
    // would be worse — but disarm the send.
    if (!source) {
      setTemplateGone(true);
      return;
    }
    setTemplateGone(false);
    // Identity gate first, so the deep compare below (which stringifies the
    // whole html map) only runs when a fetch actually delivered new objects.
    if (source === lastTemplateSourceRef.current) return;
    lastTemplateSourceRef.current = source;
    const next = buildTemplateData(
      source,
      senderAddresses,
      domains,
      guest.language,
    );
    setData((prev) =>
      prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, senderAddresses, domains, guest.language, data?.emailTemplateId]);

  const manualTemplates = templates.filter((t) => !t.automaticSend);
  const hasEmail = !!guest.email?.trim();

  // Single-guest opt-in warning: consent state for THIS guest (emailOptIn is
  // the authoritative consent value; email presence is the separate warning
  // above). Built locally — no endpoint roundtrip needed for one in-scope
  // guest. Reuses the shared OptInWarningBlock renderer.
  const optInBreakdown = hasEmail
    ? {
        optedInWithEmail: guest.emailOptIn === true ? 1 : 0,
        optedOutWithEmail: guest.emailOptIn === false ? 1 : 0,
        noConsentWithEmail:
          guest.emailOptIn === null || guest.emailOptIn === undefined ? 1 : 0,
      }
    : { optedInWithEmail: 0, optedOutWithEmail: 0, noConsentWithEmail: 0 };

  const handleTemplateChange = (templateName: string) => {
    const template = manualTemplates.find(
      (t) => t.templateName === templateName,
    );
    if (template) {
      setData(buildTemplateData(template, senderAddresses, domains, guest.language));
      // A different template is a different mail — its attestation starts over.
      if (template.emailTemplateId !== data?.emailTemplateId) setSendClassAttested(false);
    }
  };

  const handleSend = async () => {
    if (!data) return;
    if (attestationMissing) return;
    setSendLoading(true);
    setError("");

    try {
      const jobParams = {
        whitelist: [guest.guestId!],
        blacklist: [],
        filters: [],
        emailId: data.emailId,
        emailDomainId: data.emailDomainId,
        domainName: data.domainName,
        emailTemplateId: data.emailTemplateId,
        markAsContacted,
        sendName: sendName.trim() || defaultSendName,
        // Inside the signed payload (useClientRequestId).
        ...sendClassParams(sendClass, sendClassAttested, locale),
      };
      const response = await submitBackgroundJob(eventId, "SEND_EMAILS", {
        ...jobParams,
        clientRequestId: clientRequestId.current(jobParams),
      });

      if (
        typeof response === "object" &&
        "isServerError" in response &&
        response.isServerError
      ) {
        // Key kept: the retry replays it and the backend dedupes.
        setError(response.message ? response : "unknown");
        setSendLoading(false);
        return;
      }

      // Success: retire the key so a next send from this dialog is a new job.
      clientRequestId.consume();
      addJob({
        jobId: response as string,
        eventId,
        eventName,
        taskType: "SEND_EMAILS",
        totalItems: 1,
      });
      completeStep("send_first_email");
      setSendLoading(false);
      onClose();
    } catch {
      setError("unknown");
      setSendLoading(false);
    }
  };

  // Language options for the selected template
  const allLanguageCodes = data
    ? [
        ...new Set(
          // Declared tags PLUS the content map's own keys — the worker never
          // reads `languages`, so a template whose html holds a variant that
          // `languages` never declared is still a template this guest can be
          // rendered from (see PSendEmail for the gallery case).
          [data.primaryLanguage, ...data.languages, ...Object.keys(data.html ?? {})].filter(
            Boolean,
          ),
        ),
      ]
    : [];
  const translatedLanguageOptions = allLanguageCodes.map((lang) => langName(lang));
  const translatedToCodeMap: Record<string, string> = {};
  allLanguageCodes.forEach((lang) => {
    translatedToCodeMap[langName(lang)] = lang;
  });

  // TEST-MAIL language. This used to be `data.primaryLanguage`, moved around by
  // a picker in the right-hand column labelled "Language" — which read as though
  // it steered the SEND. It never did: `handleSend` carries no language, and the
  // worker renders this guest in their own `guest.language`. The pick is derived
  // against the CURRENT template so it cannot survive a template switch into a
  // language that template does not speak (same rule as PSendEmail).
  //
  // Seeded from THIS guest while the operator has not picked: the worker
  // renders a test send in `languageOverride` only, never in the example
  // guest's own language, while the preview beside it renders in
  // `guest.language`. The seed is `subjectEffLang` — the MAP-resolved key the
  // preview and the subject already use — not the raw tag, or an en-CA guest
  // over {en-US, en-GB} is seeded one variant and previewed the other. A
  // deliberate pick still wins.
  const effectiveTestMailLanguage = data
    ? resolveLangKey(
        allLanguageCodes,
        testMailLanguage || subjectEffLang,
        data.primaryLanguage,
      )
    : "";
  // This guest rides along as the example guest only for operators who may
  // mint their check-in token: the worker's example-guest lane mails a real
  // signed QR and live accept/decline and portal links, and the backend gates
  // it on `sent-emails:send` alone. Without the capability the send stays on
  // the raw lane, as it always was.
  const canMintSample = capabilities.can("guests:read.qr-token");
  const testMailSampleGuestId = canMintSample ? guest.guestId : undefined;
  // Named under the test-mail row whenever the guest rides along, falling
  // back to the address and then the id so the notice never goes missing.
  const testMailSampleName =
    [guest.firstName, guest.lastName]
      .map((s) => s?.trim())
      .filter(Boolean)
      .join(" ") ||
    guest.email?.trim() ||
    guest.guestId ||
    "";

  // C — what the BUILDER can edit and keep. Narrower than the union above on
  // purpose: `AppBarEmailBuilder` builds its language dropdown from this set and
  // rebuilds the whole `html` map from it on any save with editor changes, so a
  // key that lives only in `html` is neither selectable nor survivable there.
  // The chip may name such a key (the worker renders it); the deep link may not
  // promise it.
  const declaredCodes = data
    ? [...new Set([data.primaryLanguage, ...data.languages])].filter(Boolean)
    : [];

  // What this ONE recipient will actually receive. Note `data.primaryLanguage`
  // is ALREADY the guest-matched language in this dialog (buildTemplateData is
  // called WITH guest.language), so the fallback we name has to come from
  // `truePrimaryLanguage` — otherwise every recipient would look like a match.
  const recipientLanguage =
    data && allLanguageCodes.length > 1
      ? resolveRecipientLanguage(data.html, guest.language, data.truePrimaryLanguage)
      : null;

  /**
   * Cancel/Send. ONE instance, placed differently by width: inline at the end
   * of the controls column from `sm` up (unchanged), and as a pinned footer
   * below `sm`, where the full-height mail preview sits underneath and would
   * otherwise put Send a whole email away from the reader.
   *
   * Deliberately not rendered twice with a `hidden sm:flex` pair: both copies
   * exist in the DOM under jsdom and Playwright strict mode, and this dialog's
   * tests query the Send button by name.
   */
  const isSmUp = useMediaQuery(SM_UP_QUERY);

  const actionRow = (
    <div className="flex flex-row gap-3 justify-end sm:mt-auto">
                  <SecondaryButton
                    text={t("cancel")}
                    width="w-max"
                    onClick={onClose}
                  />
                  <PrimaryButton
                    text={t("send")}
                    width="w-max"
                    disabled={
                      // Opted-out guest → the consumer's marketing opt-in
                      // gate skips the send server-side; the warning block
                      // above explains why the button is locked. An
                      // ORGANISATIONAL send passes that gate, so the lock is
                      // MARKETING-only — that class needs the tick instead.
                      !hasEmail ||
                      !data?.emailTemplateId ||
                      templateGone ||
                      // Same worker-side hard failure as the bulk lane: this
                      // dialog submits the identical SEND_EMAILS job with an
                      // emailDomainId.
                      senderBlocked ||
                      (!organisational && guest.emailOptIn === false)
                    }
                    // The missing tick does not disable: the click is the
                    // send attempt that flags the box (a11y).
                    ariaDescribedBy={
                      attestationHintShown
                        ? "send-guest-class-attestation-hint"
                        : undefined
                    }
                    loading={sendLoading}
                    onClick={attestationMissing ? flagMissingAttestation : handleSend}
                  />
    </div>
  );

  return createPortal(
    <motion.div
      className="fixed left-0 top-0 bg-black/50 flex items-end sm:items-center justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none h-dvh w-screen z-200"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          onClose();
        }
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="w-full sm:w-auto flex justify-center"
        initial={SHEET_INITIAL}
        animate={SHEET_ANIMATE}
        exit={SHEET_EXIT}
        transition={SHEET_SPRING}
      >
        <Card
          icon={IconX}
          iconActionOnClick={onClose}
          padding="p-4 sm:p-6"
          surfaceColor="surface-card"
          rounded="rounded-t-3xl sm:rounded-3xl"
          width="w-full sm:w-270 sm:max-w-[min(90vw,calc(100vw-var(--ai-rail-push)-2rem))]"
          height="h-min max-h-[90dvh]"
          className="z-50"
          justify="space-y-8"
          header={t("sendMailToGuest")}
        >
          {fetchLoading ? (
            <div className="flex items-center justify-center h-80">
              <LoadingSpinner />
            </div>
          ) : manualTemplates.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8">
              <div className="max-w-80 flex flex-col items-center justify-center">
                <VideoPlayerRiv
                  width="w-30 sm:w-40 lg:w-50"
                  height="h-30 sm:h-40 lg:h-50"
                  videoName="NoEmailTemplates"
                />
                <p className="mt-3 body-1-bold text-center">
                  {t.rich("noTemplates", {
                    messaging: (chunks) => (
                      <a
                        href={`/event/${eventId}/messaging/mail-templates`}
                        className="underline hover:font-primary"
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              </div>
            </div>
          ) : (
            <>
            {/* Flex column below md, grid only from md up. As a one-column
                GRID inside a bounded height the two auto rows split the space
                equally (326px each here) while the children kept
                `overflow: visible` — so both columns spilled out of their row
                and drew on top of each other. A flex column sizes each child to
                its content and lets this container scroll instead. */}
            <div
              // `overflow-x-hidden` below md, and it is load-bearing: with
              // `overflow-y-auto` alone the spec forces the other axis to `auto`
              // too, which makes this a HORIZONTAL scroll container as well. It
              // has all of 4px to scroll — the preview's `-mx-1` shadow gutter —
              // but 4px is enough for iOS to treat a sideways drag as a scroll
              // and rubber-band the whole form. Measured off a screen recording
              // at 393px: the content swung up to 109px to the right and sprang
              // back, every time, while sitting at scrollLeft 0 throughout.
              // `md:overflow-visible` still resets BOTH axes, so the desktop
              // grid is untouched.
              //
              // `overscroll-contain` for the other axis: scrolling to the end of
              // this column used to hand the rest of the gesture to the layers
              // underneath (the guest detail, and the list behind that). The
              // dialog is portaled to `body` and has no scrollable ancestor at
              // all, so nothing in the DOM chain could absorb it.
              className="flex flex-col md:grid md:grid-cols-2 gap-4 sm:gap-8 flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain md:overflow-visible"
            >
              {/* Left column: preview. `order` puts the primary control (the
                  template picker) above the preview on a phone — in one column
                  the DOM order IS the reading order, and the test-mail row plus
                  a full email render otherwise stand between the user and the
                  choice of what actually gets sent. */}
              <div className="flex flex-col gap-6 min-h-0 shrink-0 md:shrink order-last md:order-none md:overflow-y-auto">
                {/* The test-mail row is an escape hatch, not part of the send: an
                    address, a language and a button that reach one person and
                    change nothing about what the campaign does. Behind a quiet
                    disclosure it stops standing between the template picker and
                    the preview, which is what the dialog is actually for.

                    The title is the section's own label, so the address field
                    inside is labelled "To" rather than repeating it. Both keys
                    already exist — a new one would have to be added to 84
                    locale files and would move the parity ratchet for every one
                    of them. */}
                {data?.emailTemplateId && (
                  <CollapsibleSection
                    title={t("sendTestMail")}
                    variant="quiet"
                    bodyClassName="pt-3"
                    testId="send-guest-test-mail"
                  >
                  {/* flex-wrap so the picker + button drop below the address
                      input on a narrow viewport instead of crushing it. */}
                  <div className="flex flex-row flex-wrap items-end gap-2.5">
                    <FormInput
                      label={t("to")}
                      id="sendTestMail"
                      value={testMailValue}
                      width="flex-1 min-w-[10rem]"
                      error={testMailError}
                      onChangeAction={handleTestMailChange}
                    />
                    {allLanguageCodes.length > 1 && (
                      <OptionInput
                        label={tSend("testLanguage")}
                        inputRef={languageInputRef}
                        height="h-9"
                        border="outline-secondary focus:border focus:outline-focus-bam border focus:outline-hidden"
                        options={translatedLanguageOptions}
                        sortOptions={true}
                        // Guarded: `langName` ends in `Intl.DisplayNames.of("")`,
                        // which throws — reachable via a template with no
                        // primaryLanguage at all.
                        defaultValue={
                          effectiveTestMailLanguage
                            ? langName(effectiveTestMailLanguage)
                            : ""
                        }
                        required={true}
                        width="w-max"
                        setParent={(translatedLang: string) => {
                          const langCode = translatedToCodeMap[translatedLang];
                          if (langCode) setTestMailLanguage(langCode);
                        }}
                      />
                    )}
                    <PrimaryButton
                      loading={testMailLoading}
                      width="w-max"
                      height="h-9"
                      text={t("send")}
                      // A deleted template is not only a dead send: the
                      // worker's template-missing branch writes a SKIPPED
                      // audit row for the example guest even on a test send.
                      disabled={templateGone}
                      ariaDescribedBy={
                        templateGone ? "send-guest-template-gone" : undefined
                      }
                      onClick={() =>
                        // This guest rides along as the example guest, so the
                        // test mail IS the preview: variables, conditional
                        // blocks and the QR resolve for them instead of
                        // arriving as literal `{{firstName}}` and a dummy code.
                        handleSendTestMail(
                          data.emailTemplateId,
                          effectiveTestMailLanguage,
                          testMailSampleGuestId,
                        )
                      }
                    />
                  </div>
                  {/* Only when the guest actually rides along: the line is a
                      warning about what the mail carries, not decoration. */}
                  {testMailSampleGuestId && (
                    <p className="body-3-regular font-tertiary mt-2">
                      {tSend("testMailRendersFor", { name: testMailSampleName })}
                    </p>
                  )}
                  </CollapsibleSection>
                )}
                {data?.emailTemplateId && (
                  <div className="flex flex-col surface-tertiary px-4 py-3 rounded-lg">
                    <div className="flex flex-row min-w-0 break-all body-2-medium">
                      {t("subject")}:{" "}
                      {replaceVariablesInText(
                        resolveLangValue(
                          data.mailSubject,
                          // Raw guest language against the SUBJECT map — see the
                          // twin in PSendEmail; the worker resolves the subject
                          // independently of the body's effLang.
                          guest.language,
                          data.truePrimaryLanguage,
                          "",
                        ),
                        enrichedGuest,
                        subjectCtx,
                      )}
                    </div>
                    <SenderFromLine
                      className="flex flex-row min-w-0 break-all body-2-medium"
                      label={t("from")}
                      sender={effectiveSender}
                      unavailableText={tSend("senderUnavailable")}
                      replyToMail={data.replyToMail}
                    />
                    <div
                      className={`flex flex-row body-2-medium ${
                        !hasEmail ? "font-error" : ""
                      }`}
                    >
                      {t("to")}:{" "}
                      {hasEmail ? guest.email : t("noEmail")}
                    </div>
                    {/* Which language this guest receives — from their own
                        profile, or the template's fallback. Only worth saying
                        when the template actually HAS more than one variant. */}
                    {/* `?.code`, not just the object: the chip renders null when
                        the template holds no content at all (the worker then
                        sends its "No content available" placeholder), which left
                        a bare "Language:" label with nothing after it. */}
                    {recipientLanguage?.code && (
                      <div className="flex flex-row items-center gap-1 body-2-medium">
                        {t("language")}:{" "}
                        <RecipientLanguageChip
                          language={recipientLanguage}
                          guestLanguage={guest.language}
                        />
                      </div>
                    )}
                  </div>
                )}
                {data?.emailTemplateId && guest?.guestId && (
                  <AttachmentPreflightWarningBlock
                    warnings={preflightWarnings}
                    loading={preflightLoading}
                  />
                )}
                {data?.emailTemplateId && (
                  <AttachmentPreview
                    eventId={eventId}
                    emailTemplateId={data.emailTemplateId}
                    guest={guest}
                  />
                )}
                <div className="md:flex-1 md:min-h-0 md:overflow-y-auto overflow-x-hidden -mx-1 px-1 -my-1 py-1">
                  {data && (
                    <Preview
                      data={data}
                      guests={[guest]}
                      currentGuest={0}
                    />
                  )}
                </div>
              </div>

              {/* Right column: controls */}
              <div className="flex flex-col gap-8 min-h-0 shrink-0 md:shrink md:overflow-y-auto">
                <div className="flex flex-col gap-1">
                  <OptionInput
                    label={t("selectTemplate")}
                    inputRef={templateInputRef}
                    defaultValue={data?.templateName ?? ""}
                    options={manualTemplates.map(
                      (template) => template.templateName ?? "",
                    )}
                    sortOptions={true}
                    setParent={handleTemplateChange}
                    required={true}
                  />
                  {/* One click to the place where a missing language variant is
                      actually fixed. Plain anchor, not a router <Link>: this
                      modal renders in tests that mock react-router-dom. */}
                  {data?.emailTemplateId && (
                    <a
                      href={`/email-builder/${eventId}/${data.emailTemplateId}/configurator${
                        effectiveTestMailLanguage &&
                        declaredCodes.includes(effectiveTestMailLanguage)
                          ? `?lang=${encodeURIComponent(effectiveTestMailLanguage)}`
                          : ""
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      // Hidden below sm: the email builder is a desktop canvas
                      // with a drag-and-drop editor, so on a phone this link
                      // only ever led somewhere unusable. `hidden` rather than
                      // an opacity trick — an invisible link still takes taps.
                      className="hidden sm:flex items-center gap-1.5 w-max font-cta-primary hover:font-primary"
                    >
                      <IconExternalLink size={14} />
                      <span className="body-4-medium">
                        {tSend("openTemplateInBuilder")}
                      </span>
                    </a>
                  )}
                </div>
                <FormInput
                  id="send-name"
                  label={tSend("sendNameLabel")}
                  value={sendName}
                  onChangeAction={setSendName}
                  placeholder={tSend("sendNamePlaceholder")}
                  maxLength={100}
                />
                {canPickSendClass && (
                  <SendClassPicker
                    value={sendClass}
                    attested={sendClassAttested}
                    attestationHintId="send-guest-class-attestation-hint"
                    showValidation={sendClassAttempted}
                    onChange={({ sendClass: next, attested }) => {
                      // A send attempt belongs to the class it was made for: a switch
                      // starts the (unticked) box un-flagged again (B2-6).
                      if (next !== sendClass) setSendClassAttempted(false);
                      setPickedSendClass(next);
                      setSendClassAttested(attested);
                    }}
                  />
                )}
                {/* The "Language" picker that used to sit here is gone — it
                    swapped `primaryLanguage` in local state, which never reached
                    the send (see `handleSend`) and could not even move the
                    preview, because this guest HAS a language and that wins the
                    resolution outright. The recipient block on the left now
                    names the language they really get, and the test-mail picker
                    carries the only meaning this control ever had. */}

                {senderBlocked && (
                  <div
                    id="send-guest-sender-blocked"
                    className="surface-error border outline-error rounded-md px-3 py-2 flex items-center gap-2"
                  >
                    <IconAlertTriangle
                      size={14}
                      className="font-error shrink-0"
                    />
                    <span className="body-3-medium font-error">
                      {tOptIn("senderDomainUnverifiedWarning", {
                        address: blockedSenderAddress ?? "",
                      })}
                    </span>
                  </div>
                )}

                {!hasEmail && (
                  <div className="surface-error border outline-error rounded-md px-3 py-2 flex items-center gap-2">
                    <IconAlertTriangle
                      size={14}
                      className="font-error shrink-0"
                    />
                    <span className="body-3-medium font-error">
                      {t("noEmail")}
                    </span>
                  </div>
                )}

                {guest.emailUndeliverable === true && (
                  /* The OTHER silent skip: a bounced address is checked BEFORE
                     the consent gate, so this guest receives nothing even with
                     consent on file.
                     Stated here rather than by reusing EmailUndeliverableNotice
                     on purpose. This dialog only ever opens from the guest
                     detail panel, which already renders that component for the
                     same guest — a second instance meant two role="alert"s, two
                     `guest-email-undeliverable` testids (strict-mode trap for
                     the e2e spec) and two private `clearedLocally` states, so
                     clearing the bounce in the panel left this copy still
                     claiming a dead address. The cure stays in the panel; the
                     send dialog reports and writes nothing.
                     Warn-only: the flag can be stale on a list-loaded guest,
                     and locking a send behind a stale flag is the worse
                     failure. */
                  <div
                    role="alert"
                    aria-live="polite"
                    className="surface-error border outline-error rounded-md px-3 py-2 flex flex-col gap-1 shrink-0"
                  >
                    <span className="body-3-medium font-error flex items-center gap-2">
                      <IconAlertTriangle size={14} className="shrink-0" />
                      {tGuest("undeliverableTitle")}
                    </span>
                    <span className="body-3-regular font-secondary">
                      {guest.emailUndeliverableAt
                        ? tGuest("undeliverableSince", {
                            date: new Date(
                              guest.emailUndeliverableAt,
                            ).toLocaleDateString(locale),
                          })
                        : tGuest("undeliverableBody")}
                    </span>
                  </div>
                )}

                <OptInWarningBlock
                  breakdown={optInBreakdown}
                  loading={false}
                  sendClass={sendClass}
                />

                {templateGone && (
                  <div
                    id="send-guest-template-gone"
                    role="alert"
                    className="surface-error border outline-error rounded-md px-3 py-2 flex items-center gap-2"
                  >
                    <IconAlertTriangle size={14} className="font-error shrink-0" />
                    <span className="body-3-medium font-error">{tSend("templateGone")}</span>
                  </div>
                )}
                {(error || fetchError) && (
                  <div className="surface-error border outline-error rounded-md p-3">
                    {error && (
                      <p className="body-2-regular font-error">
                        {translateBackendError(tErrors, error)}
                      </p>
                    )}
                    {fetchError && (
                      <p className="body-2-regular font-error">
                        {translateBackendError(tErrors, fetchError)}
                      </p>
                    )}
                  </div>
                )}

                <SliderInput
                  label={t("markAsContacted")}
                  checked={markAsContacted}
                  setChecked={() => setMarkAsContacted((prev) => !prev)}
                  infoMessage={t("markAsContactedInfo")}
                />

                {isSmUp && actionRow}
              </div>
            </div>
            {/* Pinned below the scroller on phones — a sibling of it, not a
                child, so it does not scroll away under the preview. */}
            {!isSmUp && (
              <div className="shrink-0 border-t outline-quaternary pt-3">
                {actionRow}
              </div>
            )}
            </>
          )}
        </Card>
      </motion.div>
    </motion.div>,
    document.body,
  );
};
